<script lang="ts" setup>
    import {ref, onMounted, watch, nextTick, onBeforeUnmount} from "vue";
    import DContainer from "../atoms/DContainer.vue";
    import DText from "../atoms/DText.vue";
    import DButton from "../atoms/DButton.vue";

    import DFade from "../transitions/DFade.vue";

    import {isVisible, getEffectiveZIndex} from "@songfinch/utils/src/dom";

    const props = defineProps<{
        activeNUX: {
            placementId: string;
            text: string;
        };
    }>();

    defineEmits(["nux-accepted"]);

    const targetEl = ref<HTMLElement | null>(null);
    const tooltipPosition = ref({top: 0, left: 0});
    const isTargetVisible = ref(false);
    const nuxZIndex = ref(50);

    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const TOOLTIP_WIDTH = 300;
    const TOOLTIP_OFFSET = 16;
    const TOOLTIP_PADDING = 8;

    const updatePosition = () => {
        if (!targetEl.value) return;

        const rect = targetEl.value.getBoundingClientRect();
        const halfTooltip = TOOLTIP_WIDTH / 2;
        const viewportWidth = window.innerWidth;

        const top = rect.bottom + window.scrollY - TOOLTIP_OFFSET;
        let left = rect.left + rect.width / 2;

        if (left - halfTooltip < TOOLTIP_PADDING) {
            left = halfTooltip + TOOLTIP_PADDING;
        } else if (left + halfTooltip > viewportWidth - TOOLTIP_PADDING) {
            left = viewportWidth - halfTooltip - TOOLTIP_PADDING;
        }

        tooltipPosition.value = {top, left};
        isTargetVisible.value = isVisible(targetEl.value);
        nuxZIndex.value = getEffectiveZIndex(targetEl.value) + 1;
    };


    watch(
        () => props.activeNUX.placementId,
        async (placementId) => {
            targetEl.value = document.getElementById(placementId);
            await nextTick();
            updatePosition();

            resizeObserver?.disconnect();
            if (targetEl.value) resizeObserver?.observe(targetEl.value);
        },
        {immediate: true}
    );

    onMounted(() => {
        // Watch for DOM changes
        mutationObserver = new MutationObserver(async () => {
            targetEl.value = document.getElementById(props.activeNUX.placementId);
            if (targetEl.value) {
                updatePosition();
            }
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });

        // Resize observer to watch target movement/size changes
        resizeObserver = new ResizeObserver(updatePosition);
        if (targetEl.value) {
            resizeObserver.observe(targetEl.value);
        }

        updatePosition();
    });

    onBeforeUnmount(() => {
        mutationObserver?.disconnect();
        resizeObserver?.disconnect();
    });
</script>

<template>
    <Teleport v-if="targetEl && isTargetVisible" to="body">
        <DFade>
            <DContainer
                class="d-nux"
                :style="{
                    position: 'absolute',
                    top: `${tooltipPosition.top + 32}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: 'translateX(-50%)',
                    zIndex: nuxZIndex,
                }"
            >
                <svg
                    class="d-nux-arrow"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                >
                    <path
                        d="M 24 22.917969 L 0 22.917969 L 9.304688 2.664062 C 10.402344 0.273438 13.597656 0.273438 14.695312 2.664062 Z M 24 22.917969"
                        fill="currentColor"
                    />
                </svg>
                <DText class="d-nux-text" variant="p-3">
                    {{activeNUX?.text}}
                </DText>
                <DButton
                    :data-test-id="`DNux-${activeNUX?.placementId}-close`"
                    class="d-nux-close"
                    theme="dark"
                    size="small"
                    variant="link"
                    label="Got It"
                    @click="$emit('nux-accepted')"
                />
            </DContainer>
        </DFade>
    </Teleport>
</template>

<style lang="scss" scoped>
.d-nux {
    position: relative;
    display: inline-grid;
    gap: 0.5rem;
    width: fit-content;
    max-width: 300px;
    min-width: 250px;
    padding: 1.25rem 1.25rem 0.625rem;
    border-radius: var(--radius-16);
    color: var(--white);
    background-color: var(--vivid-blue);
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
}

.d-nux-arrow {
    color: var(--vivid-blue);
    position: absolute;
    top: -.75rem;
    left: 50%;
    transform: translateX(-50%);
}

.d-nux-text {
  width: fit-content;
  min-width: 250px;
}

.d-nux-close {
  justify-self: flex-end;
}
</style>
